import useMediaQuery from '@material-ui/core/useMediaQuery'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useHistory, useParams } from 'react-router'
import { useLocation } from 'react-router-dom'

import LoaderWrapper from '@flatsy/core/Loader/Wrapper'
import { ButtonError, ButtonSecondaryOutlined } from '@flatsy/core/MaterialUi/Button'
import { Header1 } from '@flatsy/core/MaterialUi/Typo'
import FlatsyTheme from '@flatsy/core/MuiTheme/flatsyTheme'
import theme from '@flatsy/core/MuiTheme/theme'
import { ApplicationBookingState } from '@flatsy/types/Applications'
import { CancellationReason } from '@flatsy/types/Applications/CancellationReason'
import * as ROUTES from 'src/constants/Routes'
import { useApplicationBooking } from 'src/hooks/ApplicationBooking'
import { useApplicationDetails } from 'src/hooks/ApplicationDetails'
import { useAsset } from 'src/hooks/Asset'
import { useAppDispatch } from 'src/redux'
import { applicationSlice, deleteApplication } from 'src/redux/slices/Application'
import { updateApplicationInterest } from 'src/redux/slices/Application/Action'

import ConfirmIdentity from '../ConfirmIdentity'
import BookingCancelReason from './Reason'
import { useStyles } from './Styles'
import { initialValues, renderArticle } from './Utils'

const useQuery = () => new URLSearchParams(useLocation().search)

const Cancel: React.FC = (): React.ReactElement => {
  const { assetId } = useParams<{ assetId: string }>()
  const { applicationId } = useParams<{ applicationId: string }>()
  const { asset } = useAsset(assetId)
  const { application } = useApplicationDetails()
  const screenXs = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const [loadingAction, setLoadingAction] = useState(false)
  const [cancellationDetails, setCancellationDetails] = useState(initialValues)
  const [cancellationReason, setCancellationReason] = useState<CancellationReason>()
  const { booking } = useApplicationBooking({ applicationId })
  const hasBookingConfirmed =
    application.application.booking_state === ApplicationBookingState.CONFIRMED
  const getIsInterest = useQuery().get('interest')
  const isInterest = getIsInterest === 'true'
  const [displayConfirmIdentity, setDisplayConfirmIdentity] = useState(false)
  const i18n = useTranslation()

  const handleReasonSelect = (type: CancellationReason) => () => {
    setCancellationReason(type)
  }

  const handleReasonDetailChange = (key: CancellationReason, value: string) => {
    setCancellationDetails((details) => ({ ...details, [key]: value }))
  }

  const handleReturnClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    history.push(
      generatePath(ROUTES.APPLICATION_BOOKING, {
        applicationId,
      })
    )
  }

  const submit = async (code: string): Promise<boolean> => {
    if (!cancellationReason || !applicationId) {
      return false
    }
    try {
      setLoadingAction(true)
      await dispatch(applicationSlice.actions.setInterestToFalse())
      await dispatch(
        updateApplicationInterest({
          applicationId,
          interest: isInterest,
        })
      )
      const payload = {
        applicationId,
        abandon_explaination: cancellationDetails[cancellationReason],
        abandon_reason: cancellationReason,
        authentication_code: code,
      }
      const action = await dispatch(deleteApplication(payload))
      unwrapResult(action)
      history.push(
        generatePath(ROUTES.APPLICATION_DOSSIER_CANCELLED, {
          applicationId,
        })
      )
      return true
    } catch (err) {
      return false
    } finally {
      setLoadingAction(false)
    }
  }

  if (displayConfirmIdentity) {
    return (
      <ConfirmIdentity
        submit={submit}
        applicationId={applicationId}
        setDisplayConfirmIdentity={setDisplayConfirmIdentity}
      />
    )
  }

  return (
    <div className={getIsInterest !== 'false' ? classes.flexColumn : classes.cancelInterest}>
      <div className={classes.container}>
        <div className={classes.containerTop}>
          <Header1 lowerCase>{i18n.t('withdraw_my_application')}</Header1>
          <div className={classes.containerText}>
            <img src={FlatsyTheme.image.pic} alt="trash booking cancel" className={classes.trash} />
            {(application.application.agent?.id || booking?.id) && asset && (
              <BookingCancelReason
                agent={{
                  name:
                    application.application.agent?.first_name || booking?.agent.first_name || '',
                  phoneNumber:
                    application.application.agent?.phone_number ||
                    booking?.agent.phone_number ||
                    '',
                }}
                hasBookingConfirmed={hasBookingConfirmed}
                selectedReason={cancellationReason}
                details={cancellationDetails}
                handleSelection={handleReasonSelect}
                handleDetailChange={handleReasonDetailChange}
              />
            )}
          </div>
        </div>
        <div className={classes.containerBottom}>
          <div className={classes.flex}>
            {!asset?.is_sale && !screenXs && renderArticle(classes)}
          </div>
          <div className={classes.containerButton}>
            {getIsInterest !== 'false' && (
              <ButtonSecondaryOutlined onClick={handleReturnClick}>
                {i18n.t('back_to_my_application')}
              </ButtonSecondaryOutlined>
            )}
            <ButtonError
              onClick={() => setDisplayConfirmIdentity(true)}
              disabled={!cancellationReason}
            >
              {i18n.t('cancel_my_application')}
            </ButtonError>
          </div>
        </div>
      </div>
      {!asset?.is_sale && screenXs && (
        <div className={classes.containerArticle}>{renderArticle(classes)}</div>
      )}
      {loadingAction && <LoaderWrapper />}
    </div>
  )
}

export default Cancel
